/**
 * @file     showingCalendar.ts
 * @purpose  The showing sheet's time helpers and its Add to calendar file: the
 *           next 14 days, the start-time slots, open-house wall times as 24-hour
 *           "HH:MM", the "Tue, Oct 13, 10:00 AM Pacific" line, and an RFC 5545
 *           .ics event in Pacific wall time for the requested slot.
 */
import { formatNumericDate, formatWeekdayMonthDay } from '@/utils/dateFormat';

export const SHOWING_TZ = 'America/Vancouver';

/** Start times offered, 9 AM to 6 PM Pacific. */
export const SHOWING_SLOTS = ['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'] as const;

const DAY_MS = 86_400_000;

/** The next `count` days from tomorrow, as "YYYY-MM-DD" in Pacific time. */
export function showingDays(now: Date = new Date(), count = 14): string[] {
  const out: string[] = [];
  for (let i = 1; i <= count; i++) out.push(formatNumericDate(new Date(now.getTime() + i * DAY_MS)));
  return [...new Set(out)];
}

/** "14:00" → "2:00 PM". */
export function clockLabel(hhmm: string): string {
  const [h = 0, m = 0] = hhmm.split(':').map(Number);
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

/** "2pm", "2:30pm" (formatOpenHouseTime) or "2:00 PM" → "14:00". Null when unreadable. */
export function clockFromLabel(label: string | null | undefined): string | null {
  const match = /^(\d{1,2})(?::(\d{2}))?\s*([ap])\.?m\.?$/i.exec(label?.trim() ?? '');
  if (!match) return null;
  let hour = Number(match[1]) % 12;
  if (match[3]!.toLowerCase() === 'p') hour += 12;
  return `${String(hour).padStart(2, '0')}:${match[2] ?? '00'}`;
}

/** "Tue, Oct 13, 10:00 AM Pacific". */
export function showingSlotLine(date: string, time: string, pacific = 'Pacific'): string {
  return `${formatWeekdayMonthDay(date)}, ${clockLabel(time)} ${pacific}`;
}

function icsStamp(date: string, time: string): string {
  return `${date.replaceAll('-', '')}T${time.replace(':', '')}00`;
}

function addMinutes(time: string, minutes: number): string {
  const [h = 0, m = 0] = time.split(':').map(Number);
  const total = Math.min(23 * 60 + 59, h * 60 + m + minutes);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

function escapeText(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');
}

export interface ShowingEventInput {
  date: string;
  time: string;
  /** Defaults to 30 minutes after the start. */
  endTime?: string | null;
  title: string;
  location: string;
  description: string;
  url?: string | null;
  uid: string;
  now?: Date;
}

/** One VEVENT in Pacific wall time. Calendar apps resolve the TZID. */
export function showingIcs(input: ShowingEventInput): string {
  const end = input.endTime && input.endTime > input.time ? input.endTime : addMinutes(input.time, 30);
  const stamp = (input.now ?? new Date()).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Relta//Showing request//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${input.uid}`,
    `DTSTAMP:${stamp}`,
    `DTSTART;TZID=${SHOWING_TZ}:${icsStamp(input.date, input.time)}`,
    `DTEND;TZID=${SHOWING_TZ}:${icsStamp(input.date, end)}`,
    `SUMMARY:${escapeText(input.title)}`,
    `LOCATION:${escapeText(input.location)}`,
    `DESCRIPTION:${escapeText(input.description)}`,
    ...(input.url ? [`URL:${input.url}`] : []),
    'STATUS:TENTATIVE',
    'END:VEVENT',
    'END:VCALENDAR',
  ];
  return `${lines.join('\r\n')}\r\n`;
}

/** Hands the browser the .ics file. */
export function downloadIcs(filename: string, ics: string): void {
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
